import imgEmptyNoData from 'assets/images/img_emptyview_no_data.png';
import AgencyHelper from 'features/Agency/agencyHelper';
import ChargeHelper from 'features/Charge/chargeHelper';
import DealerHelper from 'features/ManageDealer/dealerHelper';
import { AppCustomDialogHelper } from 'general/components/AppDialogCustom';
import DropdownTableConfigColumn from 'general/components/DropdownTableConfigColumn';
import KTFormSearch from 'general/components/OtherKeenComponents/Forms/KTFormSearch';
import KTDropdownFilter from 'general/components/OtherKeenComponents/KTDropdownFilter';
import KTLabel, { KTLabelSize } from 'general/components/OtherKeenComponents/KTLabel';
import KTPagination, {
    KTPaginationColor,
} from 'general/components/OtherKeenComponents/KTPagination';
import KTTable from 'general/components/OtherKeenComponents/KTTable';
import tableStyle from 'general/components/OtherKeenComponents/KTTable/tableStyle';
import KTTooltip from 'general/components/OtherKeenComponents/KTTooltip';
import CustomDateRangePicker, {
    dateFilter,
    getDateFilterOption,
} from 'general/components/Others/CustomDateRangePicker';
import EmptyView from 'general/components/Others/EmptyView';
import MenuActionDropdown from 'general/components/Others/MenuActionDropdown';
import AppColors from 'general/constants/AppColors';
import AppConstants from 'general/constants/AppConstants';
import UIHelper from 'general/helpers/UIHelper';
import Utils from 'general/utils/Utils';
import useConfigTableColumn from 'hooks/useConfigTableColumn';
import useRouter from 'hooks/useRouter';
import _ from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import AppRoute from 'general/constants/AppRoute';
import FileUploadView from 'general/components/quybka/Uploads/FileUploadView';
import DialogVoucherBatchAddView from '../DialogVoucherBatchAddView';
import ButtonFilterDropdown from 'general/components/quybka/Buttons/ButtonFilterDropdown';
import VoucherBatchListFilterView from './components/VoucherBatchListFilterView';
import DialogCreateVoucherView from '../DialogCreateVoucherView';
import PropTypes from 'prop-types';
import AppConfigs from 'general/constants/AppConfigs';

VoucherBatchListView.propTypes = {
    agencyId: PropTypes.string,
    showFullUI: PropTypes.bool,
    onRefresh: PropTypes.func,
};

const defaultColumnIds = [
    'BATCH_CODE',
    'NAME',
    'AGENCY_NAME',
    'BATCH_TYPE',
    'VOUCHER_TYPE',
    'VOUCHER_IDENTIFY',
    'VOUCHER_AMOUNT',
    'DURATION',
    'EXPIRATION_DATE',
    'QUANTITY',
    'USED_QUANTITY',
    'USE_LIMIT',
    'PREFIX',
    'STATUS',
    'FILE',
    'CREATED_BY',
    'DATE',
];

function VoucherBatchListView(props) {
    // ---- params ----
    const { agencyId, showFullUI = true, onRefresh } = props;
    const { t } = useTranslation();
    const router = useRouter();
    const { user, permissionList } = useSelector((state) => state.auth);
    const [operatorList, setOperatorList] = useState([]);
    const [filter, setFilter] = useState(() => ({ ...AgencyHelper.Filter.voucherBatchList }));
    const [filterDropdown, setFilterDropdown] = useState(() => ({ ...AgencyHelper.Filter.voucherBatchList }));
    const [voucherBatchList, setVoucherBatchList] = useState([]);
    const [total, setTotal] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const listAbortRef = useRef(null);

    const app = useSelector((state) => state.app);
    const adminRole =
        user?.role === AppConstants.userRole.admin || user?.role === AppConstants.userRole.operator;

    const fullColumns = useMemo(() => {
        return [
            {
                id: 'BATCH_CODE',
                name: UIHelper.buildTableColumn('Mã lô voucher'),
                selector: (row) => row?.voucherBatchCode ?? row?.voucherBatchId ? `VP${row?.voucherBatchId?.toString().padStart(8, '0')}` : '',
                style: tableStyle.columns.normal,
                sortable: false,
                width: '120px',
            },
            {
                id: 'NAME',
                name: UIHelper.buildTableColumn('Tên lô voucher'),
                selector: (row) => row?.name,
                style: tableStyle.columns.normal,
                sortable: false,
                minWidth: '240px',
            },
            {
                id: 'AGENCY_NAME',
                name: UIHelper.buildTableColumn('Tên đối tác'),
                minWidth: '240px',
                selector: (row) => {
                    return (
                        <KTTooltip offset={[0, 0]} text={row?.agency?.name ?? ''}>
                            <span onClick={() => {
                                Utils.openInNewTab(`${AppRoute.voucherPartnerList}/${row?.agency?.agencyId}`)
                            }} >{row?.agency?.name ?? ''}</span>
                        </KTTooltip>
                    );
                },
                style: tableStyle.columns.link,
                sortable: false,
            },
            {
                id: 'BATCH_TYPE',
                name: (
                    <div className="d-flex gap-2 align-items-center">
                        {UIHelper.buildTableColumn("Hình thức phát hành")}

                        <KTTooltip
                            text={`<span>
                                Tự nhận: Khách hàng tự nhận voucher từ kho công khai<br />
                                Đối tác: Đối tác phân phối hoặc gán voucher cho khách hàng/xe<br />
                                Nội bộ: Nhà điều hành gán voucher trực tiếp, không công khai
                            </span>`}
                            textHeader="Hình thức phát hành"
                            isHtmlContent
                        >
                            <i className="fa-regular fa-info-circle" style={{ color: AppColors.primary }} />
                        </KTTooltip>
                    </div>
                ),
                selector: (row) => AgencyHelper.Utils.getVoucherBatchTypeName(row?.batchType),
                style: tableStyle.columns.normal,
                sortable: false,
                width: '170px',
            },
            {
                id: 'VOUCHER_IDENTIFY',
                name: UIHelper.buildTableColumn('Đối tượng nhận voucher'),
                width: '140px',
                selector: (row) => AgencyHelper.Utils.getVoucherIdentityTypeName(row?.identityType),
                style: tableStyle.columns.normal,
                sortable: false,
            },
            {
                id: 'VOUCHER_TYPE',
                name: UIHelper.buildTableColumn('Loại voucher'),
                width: '140px',
                selector: (row) => AgencyHelper.Utils.getVoucherTypeName(row?.type),
                style: tableStyle.columns.normal,
                sortable: false,
            },
            {
                id: 'VOUCHER_AMOUNT',
                name: UIHelper.buildTableColumn('Giá trị voucher'),
                selector: (row) => Utils.formatNumber(row.value ?? 0),
                style: tableStyle.columns.normal,
                sortable: false,
                width: '140px',
                right: true,
            },
            {
                id: 'DURATION',
                name: UIHelper.buildTableColumn('Số ngày hiệu lực từ lúc phát hành'),
                selector: (row) => row?.duration,
                style: tableStyle.columns.normal,
                width: '170px',
                right: true,
            },
            {
                id: 'EXPIRATION_DATE',
                name: UIHelper.buildTableColumn('Hạn phát hành'),
                selector: (row) => row.expirationDate ? Utils.formatDateTimeToString(row.expirationDate, 'DD/MM/YYYY') : '',
                style: tableStyle.columns.normal,
                width: '130px',
            },
            {
                id: 'QUANTITY',
                name: UIHelper.buildTableColumn('Số lượng voucher đã mua'),
                selector: (row) => row?.quantity,
                sortable: false,
                width: '170px',
                right: true,
            },
            {
                id: 'USED_QUANTITY',
                name: UIHelper.buildTableColumn('Số lượng voucher đã phát hành'),
                selector: (row) => row?.usedQuantity,
                style: tableStyle.columns.normal,
                width: '170px',
                right: true,
            },
            {
                id: 'USE_LIMIT',
                name: UIHelper.buildTableColumn('Số lượt tối đa sử dụng của mỗi voucher'),
                selector: (row) => row?.useLimit,
                style: tableStyle.columns.normal,
                sortable: false,
                width: '170px',
                right: true,
            },
            {
                id: 'PREFIX',
                name: UIHelper.buildTableColumn('Tiền tố'),
                // selector: (row) => row?.useLimit,
                selector: (row) => row?.prefix,
                style: tableStyle.columns.normal,
                width: '150px',
            }, {
                id: 'STATUS',
                name: UIHelper.buildTableColumn('Trạng thái'),
                selector: (row) => {
                    return (
                        <KTLabel
                            text={AgencyHelper.Utils.getVoucherBatchStatusName(row.status)}
                            isInline={true}
                            size={KTLabelSize.medium}
                            additionalClassName={`label-light-${AgencyHelper.Utils.getVoucherBatchStatusColor(row.status)}`}
                        />
                    );
                },
                style: tableStyle.columns.normal,
                width: '150px',
            }, {
                id: 'FILE',
                name: UIHelper.buildTableColumn('Tệp đính kèm'),
                selector: (row) => {
                    return row?.attachedFiles?.length > 0 ? (
                        <span
                            className="hover-opacity-60 cursor-pointer"
                            onClick={() => {
                                handleViewFiles(row?.attachedFiles);
                            }}
                        >
                            {`Xem chi tiết (${row?.attachedFiles?.length})`}
                        </span>
                    ) : (
                        <></>
                    );
                },
                style: tableStyle.columns.link,
                minWidth: '150px',
            },
            {
                id: 'CREATED_BY',
                name: UIHelper.buildTableColumn('Người tạo'),
                selector: (row) => {
                    const creator = AgencyHelper.Utils.getVoucherBatchCreator(row);
                    return (
                        <KTTooltip offset={[0, 0]} text={creator?.fullname}>
                            <span
                                className="cursor-pointer hover-opacity-60"
                                onClick={() => {
                                    DealerHelper.navigateToParentAgency(creator);
                                }}
                            >
                                {creator?.fullname}
                            </span>
                        </KTTooltip>
                    );
                },
                style: tableStyle.columns.link,
                sortable: false,
                width: '200px',
            },
            {
                id: 'DATE',
                name: UIHelper.buildTableColumn('Ngày tạo'),
                selector: (row) => Utils.formatDateTimeToString(row.createdAt, 'DD/MM/YYYY - HH:mm'),
                style: tableStyle.columns.normal,
                sortable: false,
                width: '160px',
            },
            {
                name: UIHelper.buildTableColumn('Thao tác'),
                width: '130px',
                style: {
                    position: 'sticky',
                    right: 0,
                    backgroundColor: '#fff',
                    borderLeft: '1px solid #ebedfe',
                },
                wrap: true,
                cell: (row) => {
                    return (
                        <MenuActionDropdown
                            menuAction={[
                                {
                                    iconElement: (
                                        <i
                                            className="fa-regular fa-eye"
                                            style={{ color: AppColors.gray[700], fontSize: '1.1rem' }}
                                        ></i>
                                    ),
                                    text: 'Xem chi tiết',
                                    click: () => {
                                        if (showFullUI) {
                                            handleClickedRow(row);
                                        } else {
                                            Utils.openInNewTab(`${AppRoute.voucherBatchList}/${row?.voucherBatchId}`)
                                        }
                                    },
                                    show: true,
                                },
                                {
                                    iconElement: (
                                        <i
                                            className="fa-regular fa-pen-to-square"
                                            style={{ color: AppColors.gray[700], fontSize: '1.1rem' }}
                                        ></i>
                                    ),
                                    text: 'Chỉnh sửa',
                                    click: () => {
                                        handleAddNew(row);
                                    },
                                    show: adminRole && row.status === 'NEW' && !agencyId,
                                },
                                {
                                    iconElement: (
                                        <i
                                            className="fa-regular fa-ticket"
                                            style={{ color: AppColors.gray[700], fontSize: '1.1rem' }}
                                        ></i>
                                    ),
                                    text: 'Tạo voucher',
                                    click: () => {
                                        handleCreateVoucher(row);
                                    },
                                    show: adminRole && row.status === 'NEW' && !agencyId,
                                },
                                {
                                    iconElement: (
                                        <i
                                            className="far fa-square"
                                            style={{ color: AppColors.gray[700], fontSize: '1.1rem' }}
                                        ></i>
                                    ),
                                    text: 'Ngừng hoạt động',
                                    click: () => {
                                        AgencyHelper.UI.showDialogConfirmStopVoucherBatch(row, () => {
                                            getVoucherBatchList(filter);
                                        });
                                    },
                                    show: adminRole && row?.status === 'ACTIVE',
                                },
                                {
                                    iconElement: (
                                        <i
                                            className="far fa-play"
                                            style={{ color: AppColors.gray[700], fontSize: '1.1rem' }}
                                        ></i>
                                    ),
                                    text: 'Khôi phục hoạt động',
                                    click: () => {
                                        AgencyHelper.UI.showDialogConfirmActiveVoucherBatch(row, () => {
                                            getVoucherBatchList(filter);
                                        });
                                    },
                                    show: adminRole && row?.status === 'INACTIVE',
                                },
                                {
                                    iconElement: (
                                        <i
                                            className={`fa-regular fa-trash-can`}
                                            style={{ color: AppColors.gray[700], fontSize: '1.1rem' }}
                                        ></i>
                                    ),
                                    text: 'Xóa lô voucher',
                                    click: () => {
                                        AgencyHelper.UI.showDialogConfirmDeleteVoucherBatch(row, () => {
                                            getVoucherBatchList(filter);
                                        });
                                    },
                                    show: adminRole && row.status === 'NEW' && !agencyId,
                                },
                            ]}
                        />
                    );
                },
            },
        ];
    }, [t, operatorList, user, filter, adminRole]);

    const prefKey = useMemo(() => {
        const userId = user.userId;
        const pathName = router.pathname;
        const key = `${userId}_${pathName}`;
        return key;
    }, [user, router]);
    const [fullColumnIds, setFullColumnIds] = useState(
        fullColumns?.map((row) => row?.id).filter(Boolean) || []
    );
    const {
        columnIds,
        columns,
        onChangeCheckboxValue,
        saveChanges,
        resetDefaultTableColumn,
        onSelectAll,
        onUnSelectAll,
        fullColumnsSorted,
        fullColumnsIdSorted,
    } = useConfigTableColumn(prefKey, fullColumnIds, defaultColumnIds, fullColumns);

    const classifyOperators = (operators) => {
        setOperatorList(
            operators?.map((operator) => ({ operatorId: operator.operatorId, name: operator.name }))
        );
    };

    useEffect(() => {
        if (app.appConfig) {
            classifyOperators(app.appConfig.operators ?? []);
        }
    }, [app.appConfig]);

    useEffect(() => {
        if (showFullUI) {
            setFilter(AgencyHelper.Filter.voucherBatchList);
            setFilterDropdown(AgencyHelper.Filter.voucherBatchList);
        } else {
            const updatedFilter = {
                q: '',
                page: 0,
                limit: AppConfigs.defaultPageLimit,
                batchType: '',
                identityType: '',
                status: '',
                dateFilter: dateFilter.tatCaThoiGian,
                from: getDateFilterOption(dateFilter.tatCaThoiGian)[0].format('YYYY-MM-DD'),
                to: getDateFilterOption(dateFilter.tatCaThoiGian)[1].format('YYYY-MM-DD'),
            }
            setFilter(updatedFilter)
            setFilterDropdown(updatedFilter)
        }
    }, [showFullUI])

    const getVoucherBatchList = (filterParams) => {
        listAbortRef.current?.abort();
        const controller = new AbortController();
        listAbortRef.current = controller;

        setIsLoading(true);
        const params = _.pickBy(
            filterParams,
            (value) => value !== null && value !== undefined && value !== ''
        );
        if (agencyId) {
            params.agencyId = agencyId;
        }
        AgencyHelper.API.requestGetVoucherBatchList(params, { signal: controller.signal })
            .then(async (response) => {
                if (controller.signal.aborted) return;
                const voucherBatches = await AgencyHelper.Utils.enrichVoucherBatchesWithCreators(response?.voucherBatches ?? []);
                if (controller.signal.aborted) return;
                setVoucherBatchList(voucherBatches);
                setTotal(response?.total ?? 0);
            })
            .catch((error) => {
                if (AgencyHelper.API.isRequestCancelled(error)) return;
            })
            .finally(() => {
                if (!controller.signal.aborted) setIsLoading(false);
            });
    };

    useEffect(() => {
        getVoucherBatchList(filter);
    }, [filter, agencyId]);

    useEffect(() => {
        return () => {
            listAbortRef.current?.abort();
        };
    }, []);

    // ---- methods ----
    const handleClickedRow = (row) => {
        router.navigate(`${row.voucherBatchId}`);
    };

    const updateFilter = (changes) => {
        const updatedFilter = showFullUI
            ? {
                ...AgencyHelper.Filter.voucherBatchList,
                ...changes,
            }
            : {
                ...filter,
                ...changes,
            };

        if (showFullUI) {
            AgencyHelper.Filter.voucherBatchList = updatedFilter;
        }

        setFilter(updatedFilter);
        setFilterDropdown(updatedFilter);
    };

    const handleViewFiles = (files) => {
        AppCustomDialogHelper.show(
            'Tệp đính kèm',
            <FileUploadView
                pathList={files}
                onCancel={() => {
                    AppCustomDialogHelper.hide();
                }}
            />,
            'md',
            false,
            true
        );
    };

    const handleRefresh = () => {
        getVoucherBatchList(filter);
    };

    const handleAddNew = (row) => {
        AppCustomDialogHelper.show(
            !!row ? 'Cập nhật lô voucher' : 'Thêm lô voucher',
            <DialogVoucherBatchAddView
                item={row}
                agencyId={agencyId}
                onCancel={() => AppCustomDialogHelper.hide()}
                onDone={() => {
                    AppCustomDialogHelper.hide();
                    handleRefresh();
                }}
            />,
            'lg',
            false,
            true
        );
    };
    const handleCreateVoucher = (row) => {
        AppCustomDialogHelper.show(
            '',
            <DialogCreateVoucherView
                item={row}
                onCancel={() => AppCustomDialogHelper.hide()}
                onDone={() => {
                    AppCustomDialogHelper.hide();
                    handleRefresh();
                }}
            />,
            'md',
            false,
            true
        );
    };

    return (
        <div>
            {UIHelper.buildCard(
                <div className="pb-4">
                    {/* header */}
                    {showFullUI && (
                        <div className="px-4 pt-4 pb-4 d-flex align-items-center justify-content-between">
                            <div className="d-flex flex-column">
                                <span style={UIHelper.buildFontStyle('600', '18px', AppColors.gray[800])}>
                                    Danh sách lô voucher
                                </span>
                                <span style={UIHelper.buildFontStyle('600', '14px', AppColors.gray[800])}>
                                    Tổng số:{' '}
                                    <span style={UIHelper.buildFontStyle('600', '14px', AppColors.primary)}>
                                        {total != 0 ? total : voucherBatchList?.length} lô
                                    </span>
                                </span>
                            </div>
                            <div>
                                {adminRole && (
                                    <div className="d-flex align-items-center justify-content-end gap-3 flex-wrap">
                                        {UIHelper.buildGreenButton(
                                            'Thêm lô voucher',
                                            () => handleAddNew(),
                                            <i className="fa-regular fa-circle-plus" style={{ fontSize: 15 }} />
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* table */}
                    <div className="">
                        {/* filter */}
                        <div className="row m-0 p-0 px-2">
                            {/* Tìm kiếm */}
                            <div className="col mb-4 px-2 pt-1">
                                {UIHelper.buildFormLabel("Tìm kiếm")}
                                <KTFormSearch
                                    name="search-bar"
                                    placeholder="Theo mã lô voucher, tên đối tác"
                                    value={filter.q}
                                    onSubmit={(text) => {
                                        updateFilter({
                                            q: text,
                                            page: 0,
                                        });
                                    }}
                                />
                            </div>

                            {/* Hình thức phát hành */}
                            <div className="col mb-4 px-2">
                                <KTDropdownFilter
                                    label="Hình thức phát hành"
                                    filterKey="batchType"
                                    options={[
                                        { name: "Tất cả", value: "" },
                                        ...AgencyHelper.Data.voucherBatchTypeOptions,
                                    ]}
                                    selectedValue={filter.batchType}
                                    handleChange={(value) => {
                                        updateFilter({
                                            batchType: value,
                                            page: 0,
                                        });
                                    }}
                                />
                            </div>

                            {/* Đối tượng nhận voucher */}
                            <div className="col mb-4 px-2">
                                <KTDropdownFilter
                                    label="Đối tượng nhận voucher"
                                    filterKey="identityType"
                                    options={[
                                        { name: "Tất cả", value: "" },
                                        ...AgencyHelper.Data.voucherIdentityTypeOptions,
                                    ]}
                                    selectedValue={filter.identityType}
                                    handleChange={(value) => {
                                        updateFilter({
                                            identityType: value,
                                            page: 0,
                                        });
                                    }}
                                />
                            </div>

                            {/* Trạng thái lô voucher */}
                            <div className="col mb-4 px-2">
                                <KTDropdownFilter
                                    label="Trạng thái lô voucher"
                                    filterKey="status"
                                    options={[
                                        { name: "Tất cả", value: "" },
                                        ...AgencyHelper.Data.voucherBatchStatusOptions,
                                    ]}
                                    selectedValue={filter.status}
                                    handleChange={(value) => {
                                        updateFilter({
                                            status: value,
                                            page: 0,
                                        });
                                    }}
                                />
                            </div>

                            <div className="col mb-4 p-2 d-flex align-items-end gap-3">
                                {/* bo loc */}
                                <div style={{ minWidth: 95 }}>
                                    <ButtonFilterDropdown
                                        filtering={
                                            filter.q !== '' ||
                                            filter.batchType !== '' ||
                                            filter.identityType !== '' ||
                                            filter.status !== '' ||
                                            filter.dateFilter !== dateFilter.tatCaThoiGian ||
                                            filter.from !== getDateFilterOption(dateFilter.tatCaThoiGian)[0].format('YYYY-MM-DD') ||
                                            filter.to !== getDateFilterOption(dateFilter.tatCaThoiGian)[1].format('YYYY-MM-DD')

                                        }
                                        dropdownContent={
                                            <VoucherBatchListFilterView
                                                showFullUI={showFullUI}
                                                filterDropdown={filterDropdown}
                                                setFilterDropdown={setFilterDropdown}
                                                setFilter={setFilter}
                                            // onCancel={() => {
                                            //     handleRefresh();
                                            // }}
                                            // onApply={() => {
                                            //     handleRefresh();
                                            // }}
                                            />
                                        }
                                    />
                                </div>
                                {/* Cấu hình cột */}
                                <DropdownTableConfigColumn
                                    columnIds={columnIds}
                                    fullColumns={fullColumns}
                                    fullColumnIds={fullColumnsIdSorted}
                                    onChangeCheckboxValue={onChangeCheckboxValue}
                                    saveChanges={saveChanges}
                                    resetDefaultTableColumn={() => {
                                        setFullColumnIds(defaultColumnIds);
                                        resetDefaultTableColumn();
                                    }}
                                    onChangeFullColumnIds={(ids) => {
                                        setFullColumnIds(ids);
                                        const key = prefKey + "_FULL_COLUMN_IDS";
                                        localStorage.setItem(key, ids.join("|"));
                                    }}
                                    onSelectAll={onSelectAll}
                                    onUnSelectAll={onUnSelectAll}
                                />

                                <div>
                                    {adminRole && !showFullUI && UIHelper.buildGreenButton(
                                        'Thêm lô voucher',
                                        () => handleAddNew(),
                                        <i className="fa-regular fa-circle-plus" style={{ fontSize: 15 }} />
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="px-4">
                            <KTTable
                                columns={columns}
                                items={voucherBatchList}
                                selectableRows={false}
                                striped={false}
                                compactGrid={false}
                                onClickedRow={(row) => { }}
                                onSelectedRowsChanged={(rows) => { }}
                                onDoubleClickedRow={(rows) => { }}
                                noDataComponent={
                                    <EmptyView
                                        icon={imgEmptyNoData}
                                        title="Không có dữ liệu để hiển thị"
                                        text={<span>Vui lòng kiểm tra kết nối mạng hoặc tải lại trang.</span>}
                                    />
                                }
                            />
                        </div>
                    </div>

                    {/* pagination */}
                    <div className="px-4 mt-2">
                        <KTPagination
                            currentPage={filter.page + 1}
                            rowCount={total != 0 ? total : voucherBatchList?.length}
                            rowsPerPage={filter.limit}
                            currentItemInPageCount={voucherBatchList?.length}
                            onChangePage={(newPage) => {
                                updateFilter({
                                    page: parseInt(newPage) - 1,
                                });
                            }}
                            onChangeRowsPerPage={(newRowsPerPage) => {
                                updateFilter({
                                    limit: parseInt(newRowsPerPage),
                                    page: 0,
                                });
                            }}
                            loading={isLoading}
                            color={KTPaginationColor.success}
                            appendText="lô"
                        />
                    </div>
                </div>,
                false,
                !showFullUI
            )}
        </div>
    );
}

export default VoucherBatchListView;
